import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";

interface TagPickerProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  "data-testid"?: string;
}

export function TagPicker({ options, value, onChange, placeholder = "Select options...", "data-testid": testId }: TagPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter(v => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const remove = (option: string) => {
    onChange(value.filter(v => v !== option));
  };

  const availableOptions = options.filter(o => !value.includes(o));

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div
        className="min-h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer flex flex-wrap gap-1 items-center"
        onClick={() => setOpen(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setOpen(prev => !prev); }}
        aria-expanded={open}
      >
        {value.length === 0 && (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        {value.map(tag => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-1 pr-1 no-default-active-elevate"
          >
            {tag}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); remove(tag); }}
              className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 p-0.5"
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground flex-shrink-0" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-y-auto">
          {availableOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">All options selected</div>
          ) : (
            availableOptions.map(option => (
              <div
                key={option}
                className="px-3 py-2 text-sm cursor-pointer hover-elevate rounded-sm"
                onClick={() => { toggle(option); }}
                role="option"
                aria-selected={value.includes(option)}
              >
                {option}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
